import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { StatusBadge } from '@carrent/ui'
import { regionsApi, regionsAdminApi } from '@carrent/api'
import { api } from '@carrent/api'
import { isCarIcon, CarIconBadge } from '@carrent/ui'

export default function AdminRegionDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [region, setRegion] = useState(null)
  const [tab, setTab]       = useState('cars')
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    regionsAdminApi.get(id)
      .then(r => setRegion(r.data?.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [id])

  if (loading) return (
    <div><PageHeader title="Загрузка..." backTo="/admin/regions" />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div className="spinner" />
      </div>
    </div>
  )
  if (!region) return <div><PageHeader title="Регион не найден" backTo="/admin/regions" /></div>

  return (
    <div style={{ paddingBottom: 'var(--gap-xl)' }}>
      <PageHeader title={region.name} backTo="/admin/regions"
        right={
          <span style={{
            fontFamily: 'monospace', background: 'var(--accent)', color: '#fff',
            padding: '3px 10px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 700,
          }}>{region.code}</span>
        }
      />

      <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>
        {/* Сводка */}
        <div className="card" style={{ display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)', padding: '12px 0' }}>
          <SummaryCell label="🚗 Машин"     value={region.counts?.cars || 0} />
          <SummaryCell label="🚙 Водителей" value={region.counts?.drivers || 0} border />
          <SummaryCell label="🔧 Менеджеров" value={region.counts?.managers || 0} />
        </div>

        {/* Табы */}
        <div style={{ display: 'flex', gap: 4, padding: '4px',
          background: 'var(--bg)', borderRadius: 'var(--r)' }}>
          {[
            { v: 'cars',     l: '🚗 Машины'     },
            { v: 'drivers',  l: '🚙 Водители'   },
            { v: 'managers', l: '🔧 Менеджеры'  },
          ].map(t => (
            <button key={t.v} onClick={() => setTab(t.v)}
              style={{
                flex: 1, padding: '8px', fontSize: '0.82rem',
                border: 'none',
                borderRadius: 'var(--r-sm)',
                background: tab === t.v ? 'var(--accent)' : 'transparent',
                color: tab === t.v ? '#fff' : 'var(--text-muted)',
                cursor: 'pointer', fontWeight: tab === t.v ? 700 : 400,
                transition: 'all 0.15s',
              }}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === 'cars' && (
          <CarsTab regionId={id} regionName={region.name} onChange={load} />
        )}
        {tab === 'drivers' && (
          <UsersTab regionId={id} role="user" label="водитель" onChange={load} />
        )}
        {tab === 'managers' && (
          <UsersTab regionId={id} role="manager" label="менеджер" onChange={load} />
        )}
      </div>
    </div>
  )
}

function SummaryCell({ label, value, border }) {
  return (
    <div style={{ textAlign: 'center', padding: '4px',
      borderLeft: border ? '1px solid var(--border)' : 'none',
      borderRight: border ? '1px solid var(--border)' : 'none' }}>
      <p style={{ fontSize: '1.6rem', fontWeight: 800 }}>{value}</p>
      <p className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</p>
    </div>
  )
}

// ── Таб машин ────────────────────────────────────────────────────────────────
function CarsTab({ regionId, regionName, onChange }) {
  const [cars, setCars]       = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const navigate = useNavigate()

  const load = () => {
    setLoading(true)
    regionsAdminApi.regionCars(regionId)
      .then(r => setCars(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [regionId])

  return (
    <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn--primary" style={{ flex: 1 }}
          onClick={() => setShowAdd(true)}>
          + Добавить машину
        </button>
        <button className="btn btn--outline"
          onClick={() => navigate(`/admin/cars?region_id=${regionId}`)}>
          🆕 Создать новую
        </button>
      </div>

      {loading && <div className="spinner" style={{ margin: '20px auto' }} />}

      {!loading && cars.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
          <p style={{ fontSize: '2rem', marginBottom: 8 }}>🚗</p>
          <p style={{ fontSize: '0.85rem' }}>Нет машин в регионе</p>
        </div>
      )}

      {cars.map(car => (
        <Link key={car.id} to={`/admin/cars/${car.id}`}
          style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="card" style={{ padding: 'var(--gap-sm)',
            display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ width: 50, height: 50, borderRadius: 'var(--r-sm)',
              overflow: 'hidden', background: 'var(--bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {isCarIcon(car.photo_main)
                ? <CarIconBadge src={car.photo_main} size={50} style={{ borderRadius: 0 }} />
                : <span style={{ fontSize: '1.5rem' }}>🚗</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                {car.brand} {car.model}
              </p>
              <p style={{ fontFamily: 'monospace', fontSize: '0.75rem',
                marginTop: 2, color: 'var(--text-muted)' }}>{car.plate}</p>
            </div>
            <StatusBadge status={car.status} />
          </div>
        </Link>
      ))}

      {showAdd && (
        <AddCarModal regionId={regionId} regionName={regionName}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); onChange?.() }} />
      )}
    </div>
  )
}

function AddCarModal({ regionId, regionName, onClose, onAdded }) {
  const [available, setAvailable] = useState([])
  const [loading, setLoading]     = useState(true)
  const [search, setSearch]       = useState('')
  const [adding, setAdding]       = useState(null)
  const [reason, setReason]       = useState('')

  // Загружаем все машины не из этого региона
  useEffect(() => {
    setLoading(true)
    api.get('/admin/cars', { params: { per_page: 200 } })
      .then(r => {
        const all = r.data?.data?.items || []
        // фильтруем: либо без региона, либо в другом
        const others = all.filter(c => c.region_id !== Number(regionId))
        setAvailable(others)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [regionId])

  const filtered = available.filter(c => {
    if (!search) return true
    const q = search.toLowerCase()
    return c.brand?.toLowerCase().includes(q)
        || c.model?.toLowerCase().includes(q)
        || c.plate?.toLowerCase().includes(q)
  })

  const add = async (car) => {
    setAdding(car.id)
    try {
      await regionsAdminApi.transferCar(car.id, Number(regionId), reason || null)
      onAdded()
    } catch (e) {
      alert(e.response?.data?.message || 'Ошибка')
      setAdding(null)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)',
      display: 'flex', alignItems: 'flex-end', zIndex: 300 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: '100%', maxWidth: 480, margin: '0 auto',
        background: 'var(--bg-card)',
        borderRadius: 'var(--r-xl) var(--r-xl) 0 0',
        padding: 'var(--gap)',
        paddingBottom: 'calc(var(--gap) + env(safe-area-inset-bottom, 0px))',
        maxHeight: '88vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 'var(--gap-sm)' }}>
          <h3 style={{ margin: 0 }}>Добавить машину в «{regionName}»</h3>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
        </div>

        <p className="text-muted" style={{ fontSize: '0.78rem', marginBottom: 'var(--gap-sm)' }}>
          Выберите существующую машину — она будет переведена сюда из своего региона.
          История переводов сохраняется.
        </p>

        <input className="input" placeholder="Поиск по марке, модели, номеру"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 'var(--gap-sm)' }} />

        <input className="input" placeholder="Причина перевода (необязательно)"
          value={reason} onChange={e => setReason(e.target.value)}
          style={{ marginBottom: 'var(--gap-sm)', fontSize: '0.85rem' }} />

        {loading && <div className="spinner" style={{ margin: '20px auto' }} />}

        {!loading && filtered.length === 0 && (
          <p className="text-muted" style={{ textAlign: 'center', padding: 'var(--gap)' }}>
            {search ? 'Ничего не найдено' : 'Нет доступных машин для перевода'}
          </p>
        )}

        <div className="flex-col" style={{ gap: 4 }}>
          {filtered.map(car => (
            <div key={car.id} className="card" style={{
              padding: 'var(--gap-sm)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)',
                overflow: 'hidden', background: 'var(--bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {isCarIcon(car.photo_main)
                  ? <CarIconBadge src={car.photo_main} size={40} style={{ borderRadius: 0 }} />
                  : <span style={{ fontSize: '1.2rem' }}>🚗</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  {car.brand} {car.model}
                </p>
                <p className="text-muted" style={{ fontSize: '0.7rem',
                  fontFamily: 'monospace', marginTop: 2 }}>
                  {car.plate}
                  {car.region && <span style={{
                    marginLeft: 6, fontFamily: 'monospace', background: 'var(--bg)',
                    padding: '1px 5px', borderRadius: 3,
                  }}>🌍 {car.region.code}</span>}
                </p>
              </div>
              <button className="btn btn--primary"
                disabled={adding === car.id}
                onClick={() => add(car)}
                style={{ padding: '6px 12px', fontSize: '0.78rem' }}>
                {adding === car.id ? <span className="spinner" /> : '🚚 Перевести'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Таб пользователей ────────────────────────────────────────────────────────
function UsersTab({ regionId, role, label, onChange }) {
  const [users, setUsers]     = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)

  const load = () => {
    setLoading(true)
    regionsAdminApi.regionUsers(regionId, role)
      .then(r => setUsers(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [regionId, role])

  const handleRemove = async (userId) => {
    if (!confirm(`Убрать пользователя из региона?`)) return
    try {
      await regionsAdminApi.removeUserFromRegion(regionId, userId)
      load(); onChange?.()
    } catch (e) {
      alert(e.response?.data?.message || 'Ошибка')
    }
  }

  return (
    <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
      <button className="btn btn--primary"
        onClick={() => setShowAdd(true)}>
        + Добавить {label === 'водитель' ? 'водителя' : 'менеджера'}
      </button>

      {loading && <div className="spinner" style={{ margin: '20px auto' }} />}

      {!loading && users.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
          <p style={{ fontSize: '2rem', marginBottom: 8 }}>{label === 'водитель' ? '🚙' : '🔧'}</p>
          <p style={{ fontSize: '0.85rem' }}>Нет {label === 'водитель' ? 'водителей' : 'менеджеров'} в регионе</p>
        </div>
      )}

      {users.map(u => (
        <div key={u.id} className="card" style={{ padding: 'var(--gap-sm)',
          display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to={`/admin/users/${u.id}`}
            style={{ flex: 1, minWidth: 0, textDecoration: 'none', color: 'inherit' }}>
            <p style={{ fontWeight: 700, fontSize: '0.9rem' }}>{u.full_name}</p>
            <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>
              {u.phone}
            </p>
          </Link>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            {u.regions?.length > 1 && `+${u.regions.length - 1} рег.`}
          </span>
          <button onClick={() => handleRemove(u.id)}
            style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 'var(--r-sm)', padding: '4px 8px',
              cursor: 'pointer', fontSize: '0.72rem', color: 'var(--danger)' }}>
            Убрать
          </button>
        </div>
      ))}

      {showAdd && (
        <AddUserModal regionId={regionId} role={role} label={label}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); onChange?.() }} />
      )}
    </div>
  )
}

function AddUserModal({ regionId, role, label, onClose, onAdded }) {
  const [available, setAvailable] = useState([])
  const [loading, setLoading]     = useState(true)
  const [search, setSearch]       = useState('')
  const [adding, setAdding]       = useState(null)

  useEffect(() => {
    setLoading(true)
    regionsAdminApi.regionAvailableUsers(regionId, role, search)
      .then(r => setAvailable(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [regionId, role, search])

  const add = async (userId) => {
    setAdding(userId)
    try {
      await regionsAdminApi.addUserToRegion(regionId, userId)
      onAdded()
    } catch (e) {
      alert(e.response?.data?.message || 'Ошибка')
      setAdding(null)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)',
      display: 'flex', alignItems: 'flex-end', zIndex: 300 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: '100%', maxWidth: 480, margin: '0 auto',
        background: 'var(--bg-card)',
        borderRadius: 'var(--r-xl) var(--r-xl) 0 0',
        padding: 'var(--gap)',
        paddingBottom: 'calc(var(--gap) + env(safe-area-inset-bottom, 0px))',
        maxHeight: '88vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 'var(--gap)' }}>
          <h3 style={{ margin: 0 }}>
            Добавить {label === 'водитель' ? 'водителя' : 'менеджера'}
          </h3>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
        </div>

        <input className="input" placeholder="Поиск по имени или телефону"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 'var(--gap-sm)' }} />

        {loading && <div className="spinner" style={{ margin: '20px auto' }} />}

        {!loading && available.length === 0 && (
          <p className="text-muted" style={{ textAlign: 'center', padding: 'var(--gap)' }}>
            {search ? 'Никого не найдено' : 'Нет доступных пользователей'}
          </p>
        )}

        <div className="flex-col" style={{ gap: 4 }}>
          {available.map(u => (
            <div key={u.id} className="card" style={{
              padding: 'var(--gap-sm)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '0.88rem', fontWeight: 600 }}>{u.full_name}</p>
                <p className="text-muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                  {u.phone} · {u.regions?.length > 0 ? `${u.regions.length} рег.` : 'без регионов'}
                </p>
              </div>
              <button className="btn btn--primary"
                disabled={adding === u.id}
                onClick={() => add(u.id)}
                style={{ padding: '6px 14px', fontSize: '0.8rem' }}>
                {adding === u.id ? <span className="spinner" /> : '+ Добавить'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
